import { Component, ViewChild, ElementRef  } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';

import { SchedulerEvent } from '@progress/kendo-angular-scheduler';

import { AvailabilityViewService } from './availability-view.service';
import { SharedService } from '../../shared/shared.service';
import {
  AvailabilityDto,
  UserDetailsDto,
  SupporterEditDto,
  DisabledSlot,
  NewSlots,
  UserRole,
  CategoryDto,
  Language,
  SyncAndRecurrenceEmit
} from '../../shared/model';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { ConfirmationDeactivateService } from '../../shared/ConfirmationDeactivateModule/confirmationdeactivate.service';
import { MainViewService } from '../../main-view/main-view.service';
import { TranslatedNotificationService } from '../../shared/translation/translated-notification.service';

import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-availability-view',
  templateUrl: './availability-view.component.html',
  styleUrls: ['./availability-view.component.css',
    '../../shared/styles/confirm-dialog.css',
    '../../shared/styles/path-selection.css',
    '../../shared/styles/small-navigation.css']
})
export class AvailabilityViewComponent {

  @ViewChild('kendoScheduler', { static: false }) kendoScheduler: ElementRef;
  @ViewChild('kendoSchedulerDay', { static: false }) set kendoSchedulerDay(element) {
    if (element) {
      const allDay = this.kendoScheduler.nativeElement.children[0].children[1].children[0].children[0].children;
      const allDayLeft = allDay[0].children[0].children[0].children[1];
      const allDayRight = allDay[1].children[0].children[1];
      allDayLeft.style.display = 'none';
      allDayRight.style.display = 'none';
    }
  }

  @ViewChild('kendoSchedulerWeek', { static: false }) set kendoSchedulerWeek(element) {
    if (element) {
      const allDay = this.kendoScheduler.nativeElement.children[0].children[1].children[0].children[0].children;
      const allDayLeft = allDay[0].children[0].children[0].children[1];
      const allDayRight = allDay[1].children[0].children[1];
      allDayLeft.style.display = 'none';
      allDayRight.style.display = 'none';
    }
  }

  currentUser: UserDetailsDto;
  currentUserRole: UserRole = UserRole.Technician;
  selectedSupporter: SupporterEditDto;
  availabilities: AvailabilityDto[];
  fakeSlots: AvailabilityDto[];
  disabledSlots: DisabledSlot[];
  slots: SchedulerEvent[];
  addedSlots: SchedulerEvent[] = [];
  removedSlots: AvailabilityDto[] = [];
  displayWeek = true;
  selectedDate: Date;
  startTime = environment.schedulerStartHour;
  endTime = environment.schedulerEndHour;
  workDayStart = environment.workDayStartHour;
  workDayEnd = environment.workDayEndHour;
  supporters: SupporterEditDto[];
  isAlsoSupporter = false;
  categories: CategoryDto[] = [];
  languages: Language[] = [];
  daysInOneWeek = 7;

  private readonly meetingDurationInMinutes: number;
  private canAddSlots = false;
  addRecurenceModalActive = false;

  constructor(
    private availabilityService: AvailabilityViewService,
    private sharedService: SharedService,
    private notificationService: TranslatedNotificationService,
    private spinner: SpinnerService,
    private mainService: MainViewService,
    private router: Router,
    private confirmDeactivateService: ConfirmationDeactivateService) {
    this.fetchData();
    this.meetingDurationInMinutes = environment.meetingDurationInMinutes;
  }

  fetchData() {
    this.spinner.show('spinner');
    this.currentUser = this.sharedService.getLoggedUser();
    this.currentUserRole = this.sharedService.getUserRole();
    this.getCategoriesAndLanguages();
    this.sharedService.getSupporters()
    .subscribe(res => {
      this.supporters = res.filter(s => s.IsRemoved === false);
      this.isAlsoSupporter = this.currentUserIsSupporter();
      if (this.isAlsoSupporter) {
        this.availabilityService.getSupporterDetails(this.currentUser.Email)
          .subscribe(supp => {
            this.selectedSupporter = supp;
            this.setCanAddSlots();
            this.fetchAvailabilities(supp.Email, supp.Id);
          },
          err => {
            this.spinner.hide('spinner');
            this.notificationService.showExceptionError(err.error);
          });
      } else {
        this.spinner.hide('spinner');
      }
    },
    err => {
      this.notificationService.showMsgError('error.loading_supporters', 'Error while loading supporters');
    });
  }

  fetchAvailabilities(email: string, id: string) {
    return forkJoin(
      this.availabilityService.getSupporterAvailabilities(email),
      this.availabilityService.getFakeSlots(id),
      this.availabilityService.getDisabledSpots(this.selectedSupporter.Id))
        .subscribe(([availabilities, fakeSlots, unavailableSlots]) => {
          this.availabilities = availabilities.concat(fakeSlots);
          this.mapSlotsFromAvailabilities();
          this.disabledSlots = unavailableSlots.map(dataItem => (
            <DisabledSlot>{
              Start: this.parseAdjust(dataItem.Start.toString())
            })
          );
          this.spinner.hide('spinner');
        },
        err => {
          this.spinner.hide('spinner');
          this.notificationService.showExceptionError(err.error);
        });
  }

  mapSlotsFromAvailabilities() {
    this.slots = this.availabilities.map(dataItem => (
      <SchedulerEvent>{
        id: dataItem.Id,
        start: this.parseAdjust(dataItem.Start.toString()),
        end: this.parseAdjust(dataItem.End.toString()),
        title: this.createTitle(dataItem),
        ownerID: dataItem.SupporterId
      }));
  }

  private parseAdjust = (eventDate: string): Date => {
    const date = new Date(eventDate + 'Z');
    return new Date(date);
  }

  viewModeChange(displayWeek: boolean) {
    this.displayWeek = displayWeek;
  }

  emptyTimeslotClicked(date) {
    if (this.canAddSlots) {
      const index = this.addedSlots.findIndex(s => s.start.getTime() === date.getTime());

      if (index >= 0) {
        this.addedSlots.splice(index, 1);
      } else {
        if (!this.isEmptySlotDisabled(date)) {
          if (date > new Date()) {
            this.addClickedSlot(date);
          } else {
            this.notificationService.showMsgError(
              'error.slot_in_past',
              'You are trying to add availability slots in the past. That does not work.');
          }
        } else {
          this.notificationService.showMsgError(
            'error.unavailable_slot',
            'Your categories and languages are already supported in this time.');
        }
      }
    } else {
      this.notificationService.showMsgError(
        'error.profile_incomplete',
        'Your profile is incomplete. At least one category and language is required.');
    }
  }

  addClickedSlot(date) {
    const newSlot = <SchedulerEvent>{
      id: 0,
      start: date,
      end: this.getEndDate(date),
      title: '',
      ownerID: this.selectedSupporter.Email
    };
    this.addedSlots.push(newSlot);
  }

  existingSlotClicked(event) {
    const index = this.availabilities.findIndex(s => s.Id === event);

    if (index >= 0) {
      if (!this.availabilities[index].Taken) {
        this.removedSlots.push(this.availabilities[index]);
        this.availabilities.splice(index, 1);
        this.mapSlotsFromAvailabilities();
      } else {
        this.notificationService.showMsgError('error.meeting_arranged', 'There is a meeting arranged in this timeslot');
      }
    }
  }

  getEndDate(date): Date {
    const endDate = new Date();
    endDate.setTime(date);
    endDate.setMinutes(date.getMinutes() + this.meetingDurationInMinutes);

    return endDate;
  }

  isEmptySlotSelected(date): boolean {
    return this.addedSlots.findIndex(s => s.start.getTime() === date.getTime()) >= 0;
  }

  isEmptySlotDisabled(date): boolean {
    return this.disabledSlots ? this.disabledSlots.findIndex(s => s.Start.getTime() === date.getTime()) >= 0 : false;
  }

  discardChanges() {
    this.addedSlots = [];
    this.removedSlots = [];
    window.scrollTo(0, 0);
    this.router.navigateByUrl('/supporter');
  }

  openAddRecurenceModal(): void {
    this.addRecurenceModalActive = true;
  }

  closeAddRecurenceModal(addRecurence: boolean): void {
    this.addRecurenceModalActive = false;

    if (addRecurence) {
      this.saveChanges(SyncAndRecurrenceEmit.SyncAndRecurrence);
    }
    else {
      this.saveChanges(SyncAndRecurrenceEmit.OnlySync);
    }
  }

  saveChanges(triggerSync?: SyncAndRecurrenceEmit) {
    this.spinner.show('spinner');

    let addedAvailabilities;
    if(triggerSync === SyncAndRecurrenceEmit.SyncAndRecurrence || triggerSync === SyncAndRecurrenceEmit.OnlyRecurrence) {
      addedAvailabilities = this.getAddedAvailabilitiesForReccurence();
    }
    else {
      addedAvailabilities = this.getAddedAvailabilities();
    }

    if (this.removedSlots.length > 0 && addedAvailabilities.length > 0) {
      if (this.addedSlotsDifferentFromRemovedSlots()) {
        this.availabilityService.removeAvailabilitySlots(this.removedSlots.map(s => (s.Id)))
        .subscribe(() => {
          this.removedSlots = [];
          this.availabilityService.addAvailabilitySlots(this.getNewSlots(addedAvailabilities))
            .subscribe(() => {
              this.addedSlots = [];
              this.fetchAvailabilities(this.selectedSupporter.Email, this.selectedSupporter.Id);
              this.sendNotification();

              if (triggerSync === SyncAndRecurrenceEmit.SyncAndRecurrence || triggerSync === SyncAndRecurrenceEmit.OnlySync) {
                this.syncWithOutlook();
              }
            },
            err => {
              this.spinner.hide('spinner');
              this.notificationService.showExceptionError(err.error);
            });
        },
        err => {
          this.spinner.hide('spinner');
          this.notificationService.showExceptionError(err.error);
        });
      } else {
        this.discardChanges();
        this.spinner.hide('spinner');
      }
    } else {
      if (this.removedSlots.length > 0) {
        this.removeSlots(triggerSync);
      }
      if (addedAvailabilities.length > 0) {
        this.addSlots(addedAvailabilities, triggerSync);
      }
    }
  }

  getAddedAvailabilities(): AvailabilityDto[] {
    const slots = [];
    this.addedSlots.forEach(s => {
      slots.push(this.getAvailabilityDto(s));
    });

    return slots;
  }

  getAvailabilityDto(slot: SchedulerEvent): AvailabilityDto {
    return <AvailabilityDto>{
      Id: 0,
      SupporterId: this.selectedSupporter.Id,
      Start: slot.start,
      End: slot.end
    };
  }

  getAddedAvailabilitiesForReccurence(): AvailabilityDto[] {
    let slots: AvailabilityDto[] = [];
    this.addedSlots.forEach(s => {
      slots = slots.concat(this.getAvailabilityDtoForReccurence(s));
    });

    return slots;
  }

  getAvailabilityDtoForReccurence(slot: SchedulerEvent): AvailabilityDto[] {
    return [{
      Id: 0,
      SupporterId: this.selectedSupporter.Id,
      Start: new Date(slot.start.setDate(slot.start.getDate())),
      End: new Date(slot.end.setDate(slot.end.getDate()))
    },
    {
      Id: 0,
      SupporterId: this.selectedSupporter.Id,
      Start: new Date(slot.start.setDate(slot.start.getDate() + this.daysInOneWeek)),
      End: new Date(slot.end.setDate(slot.end.getDate() + this.daysInOneWeek))
    }, 
    {
      Id: 0,
      SupporterId: this.selectedSupporter.Id,
      Start: new Date(slot.start.setDate(slot.start.getDate() + this.daysInOneWeek)),
      End: new Date(slot.end.setDate(slot.end.getDate() + this.daysInOneWeek))
    }, 
    {
      Id: 0,
      SupporterId: this.selectedSupporter.Id,
      Start: new Date(slot.start.setDate(slot.start.getDate() + this.daysInOneWeek)),
      End: new Date(slot.end.setDate(slot.end.getDate() + this.daysInOneWeek))
    }
    ] as AvailabilityDto[];
  }

  removeSlots(triggerSync: SyncAndRecurrenceEmit): void {
    this.availabilityService.removeAvailabilitySlots(this.removedSlots.map(s => (s.Id)))
      .subscribe(() => {
        this.removedSlots = [];
        this.fetchAvailabilities(this.selectedSupporter.Email, this.selectedSupporter.Id);
        this.sendNotification();

        if (triggerSync === SyncAndRecurrenceEmit.SyncAndRecurrence || triggerSync === SyncAndRecurrenceEmit.OnlySync) {
          this.syncWithOutlook();
        }
      },
      err => {
        this.spinner.hide('spinner');
        this.notificationService.showExceptionError(err.error);
      });
  }

  addSlots(addedSlots: AvailabilityDto[], triggerSync: SyncAndRecurrenceEmit): void {
    this.availabilityService.addAvailabilitySlots(this.getNewSlots(addedSlots))
      .subscribe(() => {
        this.addedSlots = [];
        this.fetchAvailabilities(this.selectedSupporter.Email, this.selectedSupporter.Id);
        this.sendNotification();

        if (triggerSync === SyncAndRecurrenceEmit.SyncAndRecurrence || triggerSync === SyncAndRecurrenceEmit.OnlySync) {
          this.syncWithOutlook();
        }
      },
      error => {
        this.spinner.hide('spinner');
        this.addedSlots = [];
        this.notificationService.showExceptionError(error.error);
      });
  }

  createTitle(event): string {
    const start = this.parseAdjust(event.Start.toString());
    const end = this.parseAdjust(event.End.toString());
    const startTimeString = start.getHours() + ':' + this.getFormattedTime(start.getMinutes());
    const endTimeString = end.getHours() + ':' + this.getFormattedTime(end.getMinutes());
    return startTimeString + ' - ' + endTimeString;
  }

  getFormattedTime(minutes: number): string {
    return minutes === 0 ? '00' : minutes.toString();
  }

  changeSupporter(newSupporter: SupporterEditDto) {
    this.spinner.show('spinner');
    this.selectedSupporter = newSupporter;
    this.setCanAddSlots();
    this.fetchAvailabilities(this.selectedSupporter.Email, this.selectedSupporter.Id);
  }

  isSubmitDisabled() {
    return this.addedSlots.length === 0 && this.removedSlots.length === 0;
  }

  getNewSlots(slots): NewSlots {
    return <NewSlots>{
      Slots: slots,
      SupporterId: this.selectedSupporter.Id
    };
  }

  setCanAddSlots() {
    this.canAddSlots = this.selectedSupporter.LanguageIds.length > 0 && this.selectedSupporter.CategoryIds.length > 0;
  }

  formatHeaderDate(date: Date): string {
    return formatDate(date, 'dd/MM', 'en-US');
  }

  formatHeaderDateReturnDay(date: Date): string {
    return formatDate(date, 'EEE', 'en-US').toString();
  }

  dateTemplateString(date: Date): string {
   return 'scheduler_week_day_' + this.formatHeaderDateReturnDay(date).toString();
  }

  private currentUserIsSupporter(): boolean {

    if (this.currentUserRole === UserRole.Admin && this.supporters && this.currentUser) {
      return this.isUserAmongSupporters();
    }

    if (this.currentUserRole === UserRole.Technician && !this.isUserAmongSupporters()) {
      this.notificationService.showMsgError('error.user_is_not_supporter', 'Current user could not be found among supporters');
      return false;
    }

    return this.currentUserRole === UserRole.Technician;
  }

  isUserAmongSupporters(): boolean {
    if (this.currentUser.Email && this.currentUser.Email !== '') {
      return this.supporters
        .find(s => s.Email.toLowerCase().trim() === this.currentUser.Email.toLowerCase().trim()) !== undefined;
    }

    return this.supporters
      .find(s => s.Email.toLowerCase().trim() === this.currentUser.Email.toLowerCase().trim()) !== undefined;
  }

  canDeactivate() {
    const addedAvailabilities = this.getAddedAvailabilities();
    if (this.removedSlots.length > 0 || addedAvailabilities.length > 0) {
      return this.confirmDeactivateService.show();
    }

    return true;
  }

  isSlotTaken(id): boolean {
    return !!this.availabilities.find(a => a.Id === id && a.Taken === true);
  }

  openMeetingDetailsInTab(slotId: number) {
    const slot = this.availabilities.find(s => s.Id === slotId);
    window.open('supporter?start=' + slot.Start + '&suppId=' + slot.SupporterId, '_blank');
  }

  syncWithOutlook() {
    this.spinner.show('spinner');
    this.availabilityService.syncAvailabilityWithOutlook(this.selectedSupporter.Id)
      .pipe(
        finalize( () => this.spinner.hide('spinner'))
      )
      .subscribe(() => {
        this.notificationService.showMsgSuccess('succes.sync_and_save', 'Availability slots were saved and synchronized with Outlook successfully');
      },
      err => {
        this.notificationService.showMsgError('error.sync_and_save_error', 'Error while synchronizing slots with Outlook');
      });
  }

  private getCategoriesAndLanguages() {
      this.mainService.getAdminCategories()
      .subscribe((categories) => {
        this.categories = this.sharedService.translateCategories(categories);
        this.languages = this.sharedService.getTranslatedLanguages();
      },
      (err) => {
        this.notificationService.showMsgError('error.loading_data', 'Error while loading data');
      });
  }

  private addedSlotsDifferentFromRemovedSlots(): boolean {
    if (this.addedSlots.length === this.removedSlots.length) {
      const addedAvailabilities = this.getAddedAvailabilities();
      const differentSlots = addedAvailabilities.filter(a =>
        this.removedSlots.find(r => this.parseAdjust(r.Start.toString()).toString() === a.Start.toString()) === undefined
      );

      return differentSlots.length > 0;
    }
    return true;
  }

  private sendNotification() {
    this.availabilityService.notifySupporter(this.selectedSupporter.Id)
    .subscribe(() => {},
    err => {
      this.notificationService.showMsgInfo(
        'info.emails_not_sent',
        'Changes are saved, but server could not send notification e-mail about changes in availability slots.');
    });
  }
}
